'use client'

import * as React from 'react'

import type { MentionItem } from '../../markdown-editor.interface'
import { MentionItemButton } from './mention-item.atom'
import { getKeyboardHandler } from './mention-list-keyboard.hook'

export interface MentionListProps {
  items: MentionItem[]
  command: (item: MentionItem) => void
  query: string
}

export interface MentionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean
}

export const MentionList = React.forwardRef<MentionListRef, MentionListProps>(
  ({ items, command, query }, ref) => {
    const [selectedIndex, setSelectedIndex] = React.useState(0)
    const prevItemsRef = React.useRef<MentionItem[]>([])

    if (prevItemsRef.current !== items) {
      prevItemsRef.current = items
      if (selectedIndex !== 0) {
        setSelectedIndex(0)
      }
    }

    React.useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        const handler = getKeyboardHandler(event.key)
        if (!handler) {
          return false
        }
        return handler({ itemList: items, selectedIndex, setSelectedIndex, command })
      },
    }))

    if (items.length === 0) {
      return (
        <div className="z-50 overflow-hidden rounded-md border border-border bg-popover p-2 text-popover-foreground shadow-md">
          <p className="text-muted-foreground text-sm">No results for &quot;{query}&quot;</p>
        </div>
      )
    }

    return (
      <div className="z-50 max-h-[300px] overflow-y-auto rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md">
        {items.map((item, index) => (
          <MentionItemButton
            key={item.id}
            item={item}
            isSelected={index === selectedIndex}
            onSelect={() => command(item)}
            onHover={() => setSelectedIndex(index)}
          />
        ))}
      </div>
    )
  }
)

MentionList.displayName = 'MentionList'
